import { TileType, TILE_SIZE, setMapDimensions, DecorDef, MAP_COLS, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { MapDefinition, MAP_VALLEE } from '@/map/MapDefs';
import { placeDecorations } from '@/map/ProceduralMapGenerator';
import { SeededRandom } from '@/map/SeededRandom';
import { SlotManager } from '@/systems/SlotManager';

/**
 * Active map state — mutable, updated via setActiveMap().
 * All game systems read from these exports.
 */

/** Find nearest GROUND tile for a misplaced resource (spiral outward) */
function relocateDecor(
  d: DecorDef, grid: TileType[][], rows: number, cols: number, occupied: Set<string>,
): DecorDef | null {
  for (let radius = 1; radius <= Math.max(rows, cols); radius++) {
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (Math.abs(dr) !== radius && Math.abs(dc) !== radius) continue; // only ring
        const r = d.row + dr;
        const c = d.col + dc;
        if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
        if (grid[r][c] !== TileType.GROUND) continue;
        const key = `${c},${r}`;
        if (occupied.has(key)) continue;
        return { type: d.type, col: c, row: r };
      }
    }
  }
  return null;
}

function tileCenter(col: number, row: number): { x: number; y: number } {
  return {
    x: col * TILE_SIZE + TILE_SIZE / 2,
    y: row * TILE_SIZE + TILE_SIZE / 2,
  };
}

// ── Active map state ──
let activeMap: MapDefinition = MAP_VALLEE;

export let MAP_GRID: TileType[][] = activeMap.grid;
export let MAP_DECORATIONS: DecorDef[] = activeMap.decorations ?? [];

/** Fast spatial index: tileKey → DecorDef (for collision lookups) */
export const DECOR_INDEX = new Map<number, DecorDef>();

/** Per-decoration current HP (only for harvestable resources). Key = decorKey. */
export const DECOR_HP = new Map<number, number>();

export function decorKey(col: number, row: number): number {
  return row * MAP_COLS + col;
}

/** Get current HP for a decoration (initializes from config if first access) */
export function getDecorHp(decor: DecorDef): number {
  const key = decorKey(decor.col, decor.row);
  const existing = DECOR_HP.get(key);
  if (existing !== undefined) return existing;
  const yields = DECOR_RESOURCE_YIELDS[decor.type];
  if (!yields) return 0;
  DECOR_HP.set(key, yields.hp);
  return yields.hp;
}

/** Apply damage to a decoration. Returns remaining HP. */
export function damageDecor(decor: DecorDef, amount: number): number {
  const key = decorKey(decor.col, decor.row);
  const hp = getDecorHp(decor);
  const newHp = Math.max(0, hp - amount);
  DECOR_HP.set(key, newHp);
  return newHp;
}

/** Rebuild DECOR_INDEX from MAP_DECORATIONS */
export function rebuildDecorIndex(): void {
  DECOR_INDEX.clear();
  DECOR_HP.clear();
  for (const d of MAP_DECORATIONS) {
    DECOR_INDEX.set(decorKey(d.col, d.row), d);
  }
}

/** Get decoration at tile position (fast O(1) lookup) */
export function getDecorAt(col: number, row: number): DecorDef | undefined {
  return DECOR_INDEX.get(decorKey(col, row));
}

/** Remove a decoration from the map and index */
export function removeDecor(decor: DecorDef): void {
  const idx = MAP_DECORATIONS.indexOf(decor);
  if (idx >= 0) MAP_DECORATIONS.splice(idx, 1);
  const key = decorKey(decor.col, decor.row);
  DECOR_INDEX.delete(key);
  DECOR_HP.delete(key);
}
export let PATHS: { x: number; y: number }[][] = [];
export let BASE_POSITIONS: { x: number; y: number }[] = [];
export let SPAWN_POSITIONS: { x: number; y: number }[] = [];

function rebuildPaths(): void {
  PATHS = activeMap.paths.map(path =>
    path.map(p => tileCenter(p.col, p.row))
  );
  // Base = last waypoint of each path, deduplicated
  const baseSet = new Map<string, { x: number; y: number }>();
  for (const path of activeMap.paths) {
    const last = path[path.length - 1];
    const key = `${last.col},${last.row}`;
    if (!baseSet.has(key)) baseSet.set(key, tileCenter(last.col, last.row));
  }
  // Also include any BASE tile in the grid that isn't a path endpoint
  // (e.g. procedural maps where a base has no path routed to it)
  const grid = activeMap.grid;
  const cols = grid[0]?.length ?? 0;
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === TileType.BASE) {
        const key = `${c},${r}`;
        if (!baseSet.has(key)) baseSet.set(key, tileCenter(c, r));
      }
    }
  }
  BASE_POSITIONS = [...baseSet.values()];

  // Spawn = first waypoint of each path, deduplicated
  const spawnSet = new Map<string, { x: number; y: number }>();
  for (const path of activeMap.paths) {
    const first = path[0];
    const key = `${first.col},${first.row}`;
    if (!spawnSet.has(key)) spawnSet.set(key, tileCenter(first.col, first.row));
  }
  SPAWN_POSITIONS = [...spawnSet.values()];
}

// Initialize with default map
rebuildPaths();
rebuildDecorIndex();

/**
 * Set the active map. Call this before GameScene.create().
 * Updates MAP_GRID, PATHS, BASE_POSITIONS, SPAWN_POSITIONS,
 * and GameConfig's MAP_COLS/MAP_ROWS/GAME_WIDTH/GAME_HEIGHT.
 */
export function setActiveMap(map: MapDefinition): void {
  activeMap = map;
  MAP_GRID = map.grid;

  // Auto-generate decorations for maps that don't have any (e.g. hardcoded maps)
  if (map.decorations && map.decorations.length > 0) {
    // Relocate resources on PLACEMENT / BROKEN_PLACEMENT to valid GROUND tiles
    MAP_DECORATIONS = [];
    const occupied = new Set<string>(map.decorations.map(d => `${d.col},${d.row}`));
    for (const d of map.decorations) {
      if (DECOR_RESOURCE_YIELDS[d.type]) {
        const tile = MAP_GRID[d.row]?.[d.col];
        if (tile === TileType.PLACEMENT || tile === TileType.BROKEN_PLACEMENT) {
          const relocated = relocateDecor(d, map.grid, map.rows, map.cols, occupied);
          if (relocated) {
            occupied.add(`${relocated.col},${relocated.row}`);
            MAP_DECORATIONS.push(relocated);
          }
          continue;
        }
      }
      MAP_DECORATIONS.push(d);
    }
  } else {
    // Use a deterministic seed from the map id
    let seed = 0;
    for (let i = 0; i < map.id.length; i++) seed = (seed * 31 + map.id.charCodeAt(i)) | 0;
    const rng = new SeededRandom(seed);
    MAP_DECORATIONS = placeDecorations(rng, map.grid, map.cols, map.rows, map.paths);
  }

  setMapDimensions(map.cols, map.rows);
  rebuildPaths();
  rebuildDecorIndex();
}

export function getActiveMap(): MapDefinition {
  return activeMap;
}

// ── Completed maps tracking (best stars per map) ──
const mapBestStars: Map<string, number> = new Map();

/** Load challenge completions from the active slot into memory */
export function loadChallengeCompletions(): void {
  const saved = SlotManager.getChallengeCompletions();
  for (const [id, stars] of Object.entries(saved)) {
    mapBestStars.set(id, stars);
  }
}

/** Mark a map as completed, keeping only the best star count. Persists to slot. */
export function markMapCompleted(mapId: string, stars: number = 0): void {
  const prev = mapBestStars.get(mapId) ?? -1;
  if (stars > prev) mapBestStars.set(mapId, stars);
  else if (!mapBestStars.has(mapId)) mapBestStars.set(mapId, stars);
  // Persist to active slot
  SlotManager.updateChallengeCompletions(Object.fromEntries(mapBestStars));
}

export function isMapCompleted(mapId: string): boolean {
  return mapBestStars.has(mapId);
}

/** Get the best star count for a map (0-3), or -1 if never completed */
export function getMapBestStars(mapId: string): number {
  return mapBestStars.get(mapId) ?? -1;
}

export { ALL_MAPS } from '@/map/MapDefs';
export type { MapDefinition } from '@/map/MapDefs';
